// ============================================
// useAutoRefresh.js — TIEMPO-REAL FASE 1
// ============================================
// "Refresco inteligente" reutilizable para vistas compartidas (listas, tableros).
// Llama a `recargar()`:
//   - cuando la ventana vuelve a estar enfocada (evento 'focus')
//   - cuando la pestana vuelve a estar visible ('visibilitychange' -> 'visible')
//   - en intervalo (setInterval cada intervaloMs) SOLO si la pestana esta visible
//     y no esta pausado (ahorra bateria/datos en movil: en segundo plano no dispara)
//
// Protecciones:
//   - Evita solapamiento con un ref booleano (si ya hay una recarga en curso, salta).
//   - Lee `recargar` y `pausado` via refs para no recrear listeners/interval en cada
//     render; el efecto solo se re-suscribe si cambia intervaloMs.
//   - Limpia listeners e interval al desmontar.
//
// Firma: useAutoRefresh(recargar, { intervaloMs = 20000, pausado = false })

import { useEffect, useRef } from 'react';

export function useAutoRefresh(recargar, { intervaloMs = 20000, pausado = false } = {}) {
  // Hooks SIEMPRE arriba, sin returns previos.
  const recargarRef = useRef(recargar);
  const pausadoRef = useRef(pausado);
  const enCursoRef = useRef(false);

  // Mantener las refs al dia sin re-suscribir el efecto principal.
  useEffect(() => { recargarRef.current = recargar; }, [recargar]);
  useEffect(() => { pausadoRef.current = pausado; }, [pausado]);

  useEffect(() => {
    const visible = () =>
      typeof document === 'undefined' || document.visibilityState === 'visible';

    // Ejecuta recargar() salvo: pausado, pestana oculta, o ya hay una en curso.
    const ejecutar = async () => {
      if (pausadoRef.current) return;
      if (!visible()) return;
      if (enCursoRef.current) return;
      enCursoRef.current = true;
      try {
        await recargarRef.current?.();
      } finally {
        enCursoRef.current = false;
      }
    };

    const onFocus = () => { ejecutar(); };
    const onVisibility = () => { if (visible()) ejecutar(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    // Polling: solo cuando la pestana esta visible (el chequeo fino lo hace ejecutar()).
    const ms = Math.max(1000, Number(intervaloMs) || 20000);
    const id = setInterval(() => {
      if (!visible()) return;
      ejecutar();
    }, ms);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(id);
    };
  }, [intervaloMs]);
}

export default useAutoRefresh;
